import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import FloatingDock from '@/components/layout/FloatingDock';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { 
  Calendar as CalendarIcon, Plus, X, ChevronLeft, ChevronRight,
  Diamond, Clock, MapPin, Bell, Check, Edit2, Trash2,
  Video, Users, Star, AlertCircle, Repeat, CheckCircle
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, isToday, parseISO } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Event type configurations
const EVENT_TYPES = {
  post: { label: 'Scheduled Post', color: '#3b82f6', icon: Edit2 },
  livestream: { label: 'Live Stream', color: '#ef4444', icon: Video },
  meeting: { label: 'Meeting', color: '#8b5cf6', icon: Users },
  event: { label: 'Event', color: '#f97316', icon: CalendarIcon },
  reminder: { label: 'Reminder', color: '#eab308', icon: Bell },
  locale: { label: 'Locale Event', color: '#22c55e', icon: MapPin },
  custom: { label: 'Custom', color: '#6b7280', icon: Star }
};

const SocyyCalendar = ({ user }) => {
  const navigate = useNavigate();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  
  // Event form
  const [eventForm, setEventForm] = useState({
    title: '',
    description: '',
    event_type: 'event',
    start_time: '',
    end_time: '',
    all_day: false,
    color: '#f97316',
    reminder_minutes: [30],
    repeat: null,
    location: '',
    notes: ''
  });
  const [saving, setSaving] = useState(false);
  
  const isSupremium = user?.subscription_tier === 'supremium' || user?.is_admin;

  useEffect(() => {
    if (isSupremium) {
      fetchEvents();
      fetchUpcoming();
    }
  }, [isSupremium, currentMonth]);

  const fetchEvents = async () => {
    try {
      const start = startOfMonth(currentMonth).toISOString();
      const end = endOfMonth(currentMonth).toISOString();
      
      const response = await axios.get(
        `${API_URL}/calendar/events?start_date=${start}&end_date=${end}`,
        { withCredentials: true }
      );
      setEvents(response.data.events || []);
    } catch (error) {
      if (error.response?.status !== 403) {
        console.error('Failed to load events');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchUpcoming = async () => {
    try {
      const response = await axios.get(`${API_URL}/calendar/events/upcoming?limit=5`, { withCredentials: true });
      setUpcomingEvents(response.data.events || []);
    } catch (error) {
      console.error('Failed to load upcoming events');
    }
  };

  const createEvent = async () => {
    if (!eventForm.title || !eventForm.start_time) {
      toast.error('Title and start time are required');
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API_URL}/calendar/events`, eventForm, { withCredentials: true });
      toast.success('Event created!');
      setShowEventModal(false);
      resetForm();
      fetchEvents();
      fetchUpcoming();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create event');
    } finally {
      setSaving(false);
    }
  };

  const updateEvent = async () => {
    if (!selectedEvent) return;
    
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/calendar/events/${selectedEvent.event_id}`,
        eventForm,
        { withCredentials: true }
      );
      toast.success('Event updated!');
      setShowEventModal(false);
      setSelectedEvent(null);
      resetForm();
      fetchEvents();
      fetchUpcoming();
    } catch (error) {
      toast.error('Failed to update event');
    } finally {
      setSaving(false);
    }
  };

  const deleteEvent = async (eventId) => {
    if (!confirm('Delete this event?')) return;
    
    try {
      await axios.delete(`${API_URL}/calendar/events/${eventId}`, { withCredentials: true });
      toast.success('Event deleted');
      setShowEventModal(false);
      setSelectedEvent(null);
      fetchEvents();
      fetchUpcoming();
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  const markComplete = async (eventId) => {
    try {
      await axios.post(`${API_URL}/calendar/events/${eventId}/complete`, {}, { withCredentials: true });
      toast.success('Event marked complete!');
      fetchEvents();
      fetchUpcoming();
    } catch (error) {
      toast.error('Failed to mark complete');
    }
  };

  const resetForm = () => {
    setEventForm({
      title: '',
      description: '',
      event_type: 'event',
      start_time: '',
      end_time: '',
      all_day: false,
      color: '#f97316',
      reminder_minutes: [30],
      repeat: null,
      location: '',
      notes: ''
    });
  };

  const openCreateModal = (date = null) => {
    resetForm();
    if (date) {
      const dateStr = format(date, "yyyy-MM-dd'T'HH:mm");
      setEventForm(prev => ({ ...prev, start_time: dateStr }));
    }
    setSelectedEvent(null);
    setShowEventModal(true);
  };

  const openEditModal = (event) => {
    setSelectedEvent(event);
    setEventForm({
      title: event.title,
      description: event.description || '',
      event_type: event.event_type,
      start_time: event.start_time?.slice(0, 16) || '',
      end_time: event.end_time?.slice(0, 16) || '',
      all_day: event.all_day || false,
      color: event.color || EVENT_TYPES[event.event_type]?.color || '#6b7280',
      reminder_minutes: event.reminder_minutes || [30],
      repeat: event.repeat || null,
      location: event.location || '',
      notes: event.notes || ''
    });
    setShowEventModal(true);
  };

  // Get events for a specific day
  const getEventsForDay = (date) => {
    return events.filter(event => {
      const eventDate = parseISO(event.start_time);
      return isSameDay(eventDate, date);
    });
  };

  // Calendar days
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });

  // Non-Supremium users see upgrade prompt
  if (!isSupremium) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-white to-amber-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <div className="max-w-2xl mx-auto px-4 py-20 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center mx-auto mb-6">
              <CalendarIcon className="w-12 h-12 text-white" />
            </div>
            <h1 className="text-4xl font-heading font-black text-gray-900 dark:text-white mb-4">
              Socyy Calendar
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-400 mb-8">
              Keep track of your busy social life with <span className="text-yellow-600 font-bold">Supremium Verified</span>
            </p>
            
            <div className="bg-white dark:bg-gray-800 rounded-3xl p-8 shadow-xl border border-yellow-200 dark:border-yellow-800 mb-8">
              <h3 className="font-bold text-lg mb-4 text-gray-900 dark:text-white">Calendar Features:</h3>
              <ul className="space-y-3 text-left max-w-md mx-auto">
                {[
                  'Schedule posts, livestreams & events',
                  'Set reminders with push notifications',
                  'Import events from Locale',
                  'Recurring events support',
                  'Color-coded event types'
                ].map((feature, i) => (
                  <li key={i} className="flex items-center gap-3 text-gray-700 dark:text-gray-300">
                    <Check className="w-5 h-5 text-yellow-500" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
            
            <Button
              onClick={() => navigate('/pricing')}
              className="rounded-full bg-gradient-to-r from-yellow-500 to-amber-500 text-white font-bold px-8 h-14 text-lg hover:scale-105 transition-transform"
            >
              <Diamond className="w-5 h-5 mr-2" />
              Upgrade to Supremium - $49/month
            </Button>
          </motion.div>
        </div>
        <FloatingDock user={user} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-white to-amber-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-white/80 dark:bg-gray-900/80 backdrop-blur-lg border-b border-yellow-200 dark:border-yellow-800">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center">
                <CalendarIcon className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-heading font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  Socyy Calendar
                  <Diamond className="w-5 h-5 text-yellow-500" />
                </h1>
                <p className="text-sm text-gray-500">Manage your social schedule</p>
              </div>
            </div>
            
            <Button
              onClick={() => openCreateModal()}
              className="rounded-full bg-gradient-to-r from-yellow-500 to-amber-500 text-white font-bold"
              data-testid="create-event-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Event
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Calendar */}
          <div className="lg:col-span-2">
            <Card className="p-4 bg-white dark:bg-gray-800 rounded-2xl">
              {/* Month navigation */}
              <div className="flex items-center justify-between mb-4">
                <button
                  onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  {format(currentMonth, 'MMMM yyyy')}
                </h2>
                <button
                  onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>

              {/* Day headers */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                  <div key={day} className="text-center text-sm font-semibold text-gray-500 py-2">
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar grid */}
              <div className="grid grid-cols-7 gap-1">
                {/* Empty cells for days before month start */}
                {Array.from({ length: monthStart.getDay() }).map((_, i) => (
                  <div key={`empty-${i}`} className="h-24" />
                ))}
                
                {days.map(day => {
                  const dayEvents = getEventsForDay(day);
                  const isSelected = isSameDay(day, selectedDate);
                  const isTodayDate = isToday(day);
                  
                  return (
                    <div
                      key={day.toISOString()}
                      onClick={() => {
                        setSelectedDate(day);
                        if (dayEvents.length === 0) {
                          openCreateModal(day);
                        }
                      }}
                      className={`h-24 p-1 rounded-xl cursor-pointer transition-colors ${
                        isSelected
                          ? 'bg-yellow-100 dark:bg-yellow-900/30 ring-2 ring-yellow-400'
                          : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                      } ${isTodayDate ? 'border-2 border-yellow-400' : ''}`}
                    >
                      <div className={`text-sm font-semibold mb-1 ${
                        isTodayDate 
                          ? 'text-yellow-600' 
                          : isSameMonth(day, currentMonth) 
                          ? 'text-gray-900 dark:text-white' 
                          : 'text-gray-400'
                      }`}>
                        {format(day, 'd')}
                      </div>
                      <div className="space-y-0.5 overflow-hidden">
                        {dayEvents.slice(0, 3).map(event => {
                          const typeConfig = EVENT_TYPES[event.event_type];
                          return (
                            <div
                              key={event.event_id}
                              onClick={(e) => {
                                e.stopPropagation();
                                openEditModal(event);
                              }}
                              className="text-xs px-1.5 py-0.5 rounded truncate text-white"
                              style={{ backgroundColor: event.color || typeConfig?.color }}
                            >
                              {event.title}
                            </div>
                          );
                        })}
                        {dayEvents.length > 3 && (
                          <div className="text-xs text-gray-500 pl-1">
                            +{dayEvents.length - 3} more
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          </div>

          {/* Upcoming Events Sidebar */}
          <div className="space-y-4">
            <Card className="p-4 bg-white dark:bg-gray-800 rounded-2xl">
              <h3 className="font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-yellow-500" />
                Upcoming Events
              </h3>
              
              {upcomingEvents.length === 0 ? (
                <div className="text-center py-6 text-gray-500">
                  <CalendarIcon className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p>No upcoming events</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {upcomingEvents.map(event => {
                    const typeConfig = EVENT_TYPES[event.event_type];
                    const Icon = typeConfig?.icon || Star;
                    
                    return (
                      <div
                        key={event.event_id}
                        onClick={() => openEditModal(event)}
                        className="p-3 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-yellow-400 dark:hover:border-yellow-600 cursor-pointer transition-colors"
                      >
                        <div className="flex items-start gap-3">
                          <div
                            className="p-2 rounded-lg"
                            style={{ backgroundColor: `${event.color || typeConfig?.color}20` }}
                          >
                            <Icon
                              className="w-4 h-4"
                              style={{ color: event.color || typeConfig?.color }}
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className={`font-semibold text-gray-900 dark:text-white truncate ${event.completed ? 'line-through opacity-50' : ''}`}>
                              {event.title}
                            </h4>
                            <p className="text-sm text-gray-500">
                              {format(parseISO(event.start_time), 'MMM d, h:mm a')}
                            </p>
                            {event.location && (
                              <p className="text-xs text-gray-400 flex items-center gap-1 mt-1">
                                <MapPin className="w-3 h-3" />
                                {event.location}
                              </p>
                            )}
                          </div>
                          {!event.completed && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                markComplete(event.event_id);
                              }}
                              className="p-1 text-gray-400 hover:text-green-500"
                              title="Mark complete"
                            >
                              <CheckCircle className="w-5 h-5" />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>

            {/* Event Types Legend */}
            <Card className="p-4 bg-white dark:bg-gray-800 rounded-2xl">
              <h3 className="font-bold text-gray-900 dark:text-white mb-3">Event Types</h3>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(EVENT_TYPES).map(([key, config]) => {
                  const Icon = config.icon;
                  return (
                    <div key={key} className="flex items-center gap-2 text-sm">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: config.color }}
                      />
                      <span className="text-gray-600 dark:text-gray-400">{config.label}</span>
                    </div>
                  );
                })}
              </div>
            </Card>
          </div>
        </div>
      </div>

      {/* Event Modal */}
      <AnimatePresence>
        {showEventModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 overflow-y-auto"
            onClick={() => setShowEventModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white dark:bg-gray-800 rounded-3xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 shadow-2xl"
              data-testid="event-modal"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {selectedEvent ? 'Edit Event' : 'Create Event'}
                </h2>
                <button
                  onClick={() => setShowEventModal(false)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                {/* Event Type */}
                <div>
                  <label className="block text-sm font-semibold mb-2">Event Type</label>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(EVENT_TYPES).map(([key, config]) => {
                      const Icon = config.icon;
                      return (
                        <button
                          key={key}
                          onClick={() => setEventForm({ ...eventForm, event_type: key, color: config.color })}
                          className={`px-3 py-2 rounded-full text-sm font-semibold flex items-center gap-2 transition-colors ${
                            eventForm.event_type === key
                              ? 'text-white'
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                          }`}
                          style={eventForm.event_type === key ? { backgroundColor: config.color } : {}}
                        >
                          <Icon className="w-4 h-4" />
                          {config.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Title */}
                <div>
                  <label className="block text-sm font-semibold mb-2">Title *</label>
                  <Input
                    value={eventForm.title}
                    onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
                    placeholder="Event title"
                    className="rounded-xl"
                    data-testid="event-title-input"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-semibold mb-2">Description</label>
                  <Textarea
                    value={eventForm.description}
                    onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                    placeholder="Event details..."
                    className="rounded-xl resize-none"
                    rows={2}
                  />
                </div>

                {/* Date & Time */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-semibold mb-2">Start *</label>
                    <Input
                      type="datetime-local"
                      value={eventForm.start_time}
                      onChange={(e) => setEventForm({ ...eventForm, start_time: e.target.value })}
                      className="rounded-xl"
                      data-testid="event-start-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2">End</label>
                    <Input
                      type="datetime-local"
                      value={eventForm.end_time}
                      onChange={(e) => setEventForm({ ...eventForm, end_time: e.target.value })}
                      className="rounded-xl"
                    />
                  </div>
                </div>

                {/* Location */}
                <div>
                  <label className="block text-sm font-semibold mb-2">Location</label>
                  <Input
                    value={eventForm.location}
                    onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })}
                    placeholder="Event location"
                    className="rounded-xl"
                  />
                </div>

                {/* Reminder */}
                <div>
                  <label className="block text-sm font-semibold mb-2">Remind me</label>
                  <div className="flex flex-wrap gap-2">
                    {[15, 30, 60, 120, 1440].map(mins => (
                      <button
                        key={mins}
                        onClick={() => {
                          const current = eventForm.reminder_minutes;
                          const updated = current.includes(mins)
                            ? current.filter(m => m !== mins)
                            : [...current, mins];
                          setEventForm({ ...eventForm, reminder_minutes: updated });
                        }}
                        className={`px-3 py-1 rounded-full text-sm ${
                          eventForm.reminder_minutes.includes(mins)
                            ? 'bg-yellow-500 text-white'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        {mins < 60 ? `${mins}m` : mins < 1440 ? `${mins/60}h` : '1 day'}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-4">
                  {selectedEvent && (
                    <Button
                      variant="outline"
                      onClick={() => deleteEvent(selectedEvent.event_id)}
                      className="rounded-full text-red-500 border-red-300 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete
                    </Button>
                  )}
                  <Button
                    onClick={selectedEvent ? updateEvent : createEvent}
                    disabled={saving || !eventForm.title || !eventForm.start_time}
                    className="flex-1 rounded-full bg-gradient-to-r from-yellow-500 to-amber-500 text-white font-bold h-12"
                    data-testid="save-event-btn"
                  >
                    {saving ? 'Saving...' : selectedEvent ? 'Update Event' : 'Create Event'}
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <FloatingDock user={user} />
    </div>
  );
};

export default SocyyCalendar;
